import { useState, useEffect } from 'react';
import { socketClient } from '@/api/socket';
import { useLogsStore } from '@/store/logsStore';
import type { Log } from '@/types/log.types';

interface CriticalError {
  id: string;
  message: string;
  stackTrace?: string;
  deviceId: string;
  userId?: string;
  timestamp: string;
}

export const useRealtimeLogs = (maxLogs = 500) => {
  const logs = useLogsStore((state) => state.logs);
  const criticalErrors = useLogsStore((state) => state.criticalErrors);
  const addLog = useLogsStore((state) => state.addLog);
  const addCriticalError = useLogsStore((state) => state.addCriticalError);
  const clearLogs = useLogsStore((state) => state.clearLogs);
  const clearCriticalErrors = useLogsStore((state) => state.clearCriticalErrors);
  const setMaxLogs = useLogsStore((state) => state.setMaxLogs);

  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    setMaxLogs(maxLogs);
  }, [maxLogs, setMaxLogs]);

  useEffect(() => {
    const socket = socketClient.getSocket();
    if (!socket) {
      console.warn('Socket not initialized');
      return;
    }

    setIsConnected(socket.connected);

    const handleConnect = () => {
      setIsConnected(true);
    };

    const handleDisconnect = () => {
      setIsConnected(false);
    };

    const handleRealtimeLog = (log: Log) => {
      addLog(log);
    };

    const handleCriticalError = (error: CriticalError) => {
      console.error('Critical error received:', error);
      addCriticalError(error);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('log:realtime', handleRealtimeLog);
    socket.on('error:critical', handleCriticalError);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('log:realtime', handleRealtimeLog);
      socket.off('error:critical', handleCriticalError);
    };
  }, [addLog, addCriticalError]);

  return {
    logs,
    criticalErrors,
    isConnected,
    clearLogs,
    clearCriticalErrors,
  };
};
